import type { NotificationKind, PlannedNotification } from '@dovefiniscono/shared';
import type { LocalRecord } from '@dovefiniscono/sync';
import { type FocusSession, sessionEnd } from '../focus/progress';
import { type BreakState, breakAfter, breakEnd, type TimerSettings } from '../focus/settings';
import { addDays, formatTime, joinInstant, monthKey, toISODate } from '../lib/dates';
import { formatCents } from '../lib/money';
import type { BudgetStatus } from '../money/summary';
import { isLive } from '../tasks/board';
import { isOpen } from '../tasks/logic';

// What to tell you and when, worked out on the device from your data: budgets and streaks are
// computed here. The server only keeps the list and sends each one at its time, so a device that
// stays closed is reminded all the same; any change on any device replaces the list.

/** How far ahead: enough for a week away from the app. */
export const HORIZON_DAYS = 14;
const MORNING = '08:30';
const EVENING = '18:00';
const STREAK_TIME = '20:30';
const EVENT_LEAD_MINUTES = 15;
/** After this hour a budget waits for the morning. */
const QUIET_FROM = '21:30';
const MAX_ITEMS = 100;
/** A streak of one day is not worth a notification. */
const MIN_STREAK = 2;

export type PlanData = {
  now: Date;
  off: readonly NotificationKind[];
  tasks: readonly LocalRecord<'tasks'>[];
  events: readonly LocalRecord<'events'>[];
  obligations: readonly LocalRecord<'obligations'>[];
  invoices: readonly LocalRecord<'invoices'>[];
  purchases: readonly LocalRecord<'plannedPurchases'>[];
  /** This month's budgets. */
  budgets: readonly BudgetStatus[];
  categoryName: (id: string) => string | null;
  streak: { days: number; activeToday: boolean; isRest: (day: string) => boolean };
};

type Item = PlannedNotification;

const clip = (text: string, length: number) =>
  text.length > length ? `${text.slice(0, length - 1).trimEnd()}…` : text;

function listOf(names: readonly string[]): string {
  const shown = names.slice(0, 3).map((name) => clip(name, 60));
  const rest = names.length - shown.length;
  return rest > 0 ? `${shown.join(', ')} e altre ${rest}` : shown.join(', ');
}

/** One notification each morning, however many cards fall on that day. */
function taskDigests({ tasks, now }: PlanData, days: readonly string[]): Item[] {
  const today = toISODate(now);
  const open = tasks.filter((task) => isLive(task) && isOpen(task) && task.dueDate !== null);
  return days.flatMap((day) => {
    const due = open.filter((task) => task.dueDate === day);
    const late = day === today ? open.filter((task) => (task.dueDate as string) < today) : [];
    const all = [...due, ...late];
    const first = all[0];
    if (!first) return [];
    const single = all.length === 1;
    return [
      {
        key: `tasks:${day}`,
        kind: 'deadlines' as const,
        at: joinInstant(day, MORNING),
        title: single ? clip(first.title, 110) : `Oggi hai ${all.length} attività da chiudere`,
        body: single
          ? late.length > 0
            ? 'È in ritardo.'
            : 'Scade oggi.'
          : listOf(all.map((task) => task.title)),
        url: '/oggi',
      },
    ];
  });
}

function eventReminders({ events }: PlanData, days: readonly string[]): Item[] {
  const within = new Set(days);
  return events.flatMap((event) => {
    // Google already reminds you of its own events, on every device.
    if (event.source === 'google') return [];
    const day = toISODate(new Date(event.startsAt));
    if (!within.has(day)) return [];
    const common = {
      key: `event:${event.id}:${event.startsAt}`,
      kind: 'events' as const,
      title: clip(event.title, 110),
      url: '/calendario',
    };
    if (event.allDay) return [{ ...common, at: joinInstant(day, MORNING), body: 'Oggi.' }];
    return [
      {
        ...common,
        at: new Date(Date.parse(event.startsAt) - EVENT_LEAD_MINUTES * 60_000).toISOString(),
        body: `Tra ${EVENT_LEAD_MINUTES} minuti, alle ${formatTime(event.startsAt)}.`,
      },
    ];
  });
}

function moneyReminders(
  { obligations, invoices, purchases }: PlanData,
  days: readonly string[],
): Item[] {
  const within = new Set(days);
  const items: Item[] = [];
  const add = (key: string, at: string, title: string, body: string, url: string) =>
    items.push({ key, kind: 'money', at, title: clip(title, 110), body: clip(body, 200), url });

  for (const item of obligations) {
    if (item.settledOn || !item.dueOn) continue;
    const amount = formatCents(item.amount);
    if (item.direction === 'payable') {
      const before = addDays(item.dueOn, -1);
      if (within.has(before)) {
        add(
          `pay:${item.id}:${item.dueOn}:before`,
          joinInstant(before, EVENING),
          `Da pagare domani: ${item.name}`,
          amount,
          '/finanze',
        );
      }
    }
    if (within.has(item.dueOn)) {
      add(
        `pay:${item.id}:${item.dueOn}`,
        joinInstant(item.dueOn, MORNING),
        `${item.direction === 'payable' ? 'Da pagare' : 'Da ricevere'} oggi: ${item.name}`,
        amount,
        '/finanze',
      );
    }
  }
  for (const invoice of invoices) {
    if (invoice.paidOn || invoice.draft || !invoice.dueOn || !within.has(invoice.dueOn)) continue;
    add(
      `invoice:${invoice.id}:${invoice.dueOn}`,
      joinInstant(invoice.dueOn, MORNING),
      `Da ricevere oggi: ${invoice.client}`,
      `Fattura di ${formatCents(invoice.amount)}${invoice.subject ? `, ${invoice.subject}` : ''}`,
      '/incassi',
    );
  }
  for (const purchase of purchases) {
    if (purchase.purchasedOn || !purchase.plannedOn || !within.has(purchase.plannedOn)) continue;
    add(
      `purchase:${purchase.id}:${purchase.plannedOn}`,
      joinInstant(purchase.plannedOn, MORNING),
      `Da comprare oggi: ${purchase.name}`,
      formatCents(purchase.price),
      '/finanze',
    );
  }
  return items;
}

/** "all'80%", "al 90%". */
const atPercent = (percent: number) =>
  String(percent).startsWith('8') || percent === 11 ? `all’${percent}%` : `al ${percent}%`;

function budgetAlerts({ budgets, categoryName, now }: PlanData): Item[] {
  const today = toISODate(now);
  // During the day it goes at once (this morning is already past); at night it waits.
  const at = joinInstant(
    now.getTime() < Date.parse(joinInstant(today, QUIET_FROM)) ? today : addDays(today, 1),
    MORNING,
  );
  return budgets.flatMap(({ budget, spent, remaining, ratio }) => {
    const name = categoryName(budget.categoryId);
    if (ratio < 0.8 || name === null) return [];
    const over = ratio >= 1;
    const key = `budget:${budget.categoryId}:${monthKey(today)}:${over ? 100 : 80}`;
    const limit = formatCents(budget.monthlyLimit);
    return [
      {
        key,
        kind: 'budget' as const,
        at,
        title: over
          ? `Hai finito il budget ${clip(name, 60)}`
          : `Il budget ${clip(name, 60)} è ${atPercent(Math.floor(ratio * 100))}`,
        body: over
          ? `Hai speso ${formatCents(spent)} su ${limit}.`
          : `Hai speso ${formatCents(spent)} su ${limit}: restano ${formatCents(remaining)}.`,
        url: '/finanze',
      },
    ];
  });
}

/** The first evening the streak can break, as long as nothing happens before then. */
function streakReminder({ streak, now }: PlanData): Item[] {
  if (streak.days < MIN_STREAK) return [];
  const today = toISODate(now);
  let day = streak.activeToday || streak.isRest(today) ? addDays(today, 1) : today;
  for (let steps = 0; streak.isRest(day); steps += 1) {
    if (steps === 7) return [];
    day = addDays(day, 1);
  }
  return [
    {
      key: `streak:${day}`,
      kind: 'streak',
      at: joinInstant(day, STREAK_TIME),
      title: `La tua serie di ${streak.days} giorni è a rischio`,
      body: 'Basta una sessione di focus o un’attività chiusa prima di mezzanotte.',
      url: '/oggi',
    },
  ];
}

export function planNotifications(data: PlanData): PlannedNotification[] {
  const today = toISODate(data.now);
  const days = Array.from({ length: HORIZON_DAYS }, (_, i) => addDays(today, i));
  const all = [
    ...taskDigests(data, days),
    ...eventReminders(data, days),
    ...moneyReminders(data, days),
    ...budgetAlerts(data),
    ...streakReminder(data),
  ];
  const now = data.now.getTime();
  return all
    .filter((item) => !data.off.includes(item.kind))
    .filter((item) => item.kind === 'budget' || Date.parse(item.at) > now)
    .sort((a, b) => a.at.localeCompare(b.at) || a.key.localeCompare(b.key))
    .slice(0, MAX_ITEMS);
}

export type TimerData = {
  now: Date;
  off: readonly NotificationKind[];
  running: FocusSession | null;
  pause: BreakState | null;
  settings: TimerSettings;
  /** Sessions already done today, not counting the one running. */
  completedToday: number;
};

const inMinutes = (value: number) => `${value} ${value === 1 ? 'minuto' : 'minuti'}`;

const breakOver = (key: string, at: string): Item => ({
  key,
  kind: 'focus',
  at,
  title: 'Pausa finita',
  body: 'Si riparte quando vuoi.',
  url: '/focus',
});

/** The timer's alarm, to ring with the phone locked and the page asleep. */
export function planTimer({
  now,
  off,
  running,
  pause,
  settings,
  completedToday,
}: TimerData): PlannedNotification[] {
  if (off.includes('focus')) return [];
  const items: Item[] = [];
  if (running) {
    const end = sessionEnd(running);
    const next = breakAfter(completedToday + 1, settings);
    items.push({
      key: `focus:${running.id}`,
      kind: 'focus',
      at: end.toISOString(),
      title: 'Focus finito',
      body: `${inMinutes(running.plannedMinutes)} di focus. Ora ${next.long ? 'una pausa lunga' : 'una pausa'} di ${inMinutes(next.minutes)}.`,
      url: '/focus',
    });
    // Locked, the page cannot start the break: the end of it is planned already.
    if (settings.autoBreak) {
      const over = new Date(end.getTime() + next.minutes * 60_000).toISOString();
      items.push(breakOver(`break:${running.id}`, over));
    }
  } else if (pause) {
    items.push(breakOver(`break:${pause.startedAt}`, new Date(breakEnd(pause)).toISOString()));
  }
  return items.filter((item) => Date.parse(item.at) > now.getTime());
}
